<template>
  <div>
    <HeaderNavbar
      :title="$t('settings.signature.title')"
      :breadcrumbs="[
        { label: $t('nav.settings'), path: { name: 'settings' } },
        { label: $t('settings.signature.title'), path: { name: 'settings.signature' } },
      ]" />
    <div class="container-lead">
      <div class="mt-3 responsive-padding">
        <p class="text-muted">{{ $t('settings.signature.description') }}</p>

        <div v-if="isLoading" class="text-center text-muted py-4">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">{{ $t('common.loading') }}</span>
          </div>
        </div>

        <form v-else class="signature-form" @submit.prevent="save">
          <div class="card mb-3">
            <div class="card-body">
              <LeadTextarea
                v-model="emailSignature"
                :rows="5"
                :label="$t('settings.signature.email_label')"
                :placeholder="$t('settings.signature.email_placeholder')"
                :disabled="isSaving">
                <template #hint>{{ $t('settings.signature.email_hint') }}</template>
              </LeadTextarea>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-body">
              <LeadTextarea
                v-model="smsSignature"
                :rows="3"
                :maxlength="SMS_SIGNATURE_MAX_LENGTH"
                :label="$t('settings.signature.sms_label')"
                :placeholder="$t('settings.signature.sms_placeholder')"
                :disabled="isSaving">
                <template #hint>
                  {{ $t('settings.signature.sms_hint') }}
                  {{
                    $t('settings.signature.sms_length', {
                      count: smsSignature.length,
                      max: SMS_SIGNATURE_MAX_LENGTH,
                    })
                  }}
                </template>
              </LeadTextarea>
            </div>
          </div>

          <div class="d-flex justify-content-end">
            <LeadButton type="submit" variant="primary" :loading="isSaving">
              {{ $t('settings.signature.save') }}
            </LeadButton>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { getSignature, updateSignature } from '@/api/candidates';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadTextarea from '@/components/lead-design/LeadTextarea.vue';
import { SMS_SIGNATURE_MAX_LENGTH } from '@/helpers/candidateMessaging';
import translator from '@/locale/translator';
import type { FormUserSignature } from '@/types/candidate';

/* ---------------------------------- STATE --------------------------------- */
const router = useRouter();
const toast = useToast();
const featureStore = useCustomerFeaturesStore();

const isLoading = ref(true);
const isSaving = ref(false);
const emailSignature = ref('');
const smsSignature = ref('');

/* --------------------------------- METHODS -------------------------------- */
const applySignature = (signature: FormUserSignature) => {
  emailSignature.value = signature.email_signature ?? '';
  smsSignature.value = signature.sms_signature ?? '';
};

// failures are toasted by the API client
const load = async () => {
  isLoading.value = true;
  try {
    applySignature(await getSignature());
  } catch (error) {
    console.error('error loading signature:', error);
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    applySignature(
      await updateSignature({
        email_signature: emailSignature.value || null,
        sms_signature: smsSignature.value || null,
      })
    );
    toast.success(translator.get('settings.signature.save_success'));
  } catch (error) {
    console.error('error saving signature:', error);
  } finally {
    isSaving.value = false;
  }
};

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(() => {
  if (!featureStore.featureEcDataVault) {
    router.replace({ name: 'settings' });
    return;
  }
  load();
});
</script>

<style scoped>
.signature-form {
  max-width: 640px;
}
</style>
